<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">User Activity Logs</h1>
            </v-col>
            <v-col v-if="users.length > 0" cols="12" md="3" sm="6">
                <v-autocomplete :items="users" v-model="user" item-title="label" item-value="id"
                    @update:model-value="()=>{currentPage=1;fetchUserActivityLogs()}" label="User" clearable>
                    <!--
                        item-title carries name + email + company + id so typing
                        any of them matches. The slots below show the name on
                        its own line so the list still reads as a list of
                        people, with everything that tells two of them apart
                        underneath it.
                    -->
                    <template #item="{props: itemProps, item}">
                        <v-list-item v-bind="{...itemProps, title: item.raw.name,
                            subtitle: item.raw.detail}"/>
                    </template>
                    <template #selection="{item}">
                        <span class="text-truncate">
                            {{ item.raw.name }}
                            <span v-if="item.raw.email" class="text-medium-emphasis text-caption">
                                {{ item.raw.email }}
                            </span>
                        </span>
                    </template>
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" sm="6">
                <v-select :items="actions" v-model="action"
                    @update:model-value="()=>{currentPage=1;fetchUserActivityLogs()}" label="Action" clearable />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                        <tr>
                            <th-btn :sorted-column="sortedColumn??null" column="created_at"
                                sortable title="Timestamp" @sort="sort"/>
                            <th-btn v-if="authStore.user.type === 'admin'" :sorted-column="sortedColumn??null" column="user_id"
                                sortable title="User Name" @sort="sort"/>
                            <th-btn :sorted-column="sortedColumn??null" column="ip_address"
                                sortable title="Ip Address" @sort="sort"/>
                            <th-btn :sorted-column="sortedColumn??null" column="action"
                                sortable title="Action" @sort="sort"/>
                            <th>User Agent</th>
                            <th>Additional Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="activity in userActivityLogs" v-if="!loading && userActivityLogs.length"
                            :key="activity.id" class="text-left">
                            <td>{{ formatDateTime(activity.created_at) }}</td>
                            <td v-if="authStore.user.type === 'admin'">
                                {{ cleanName(activity.user_name || activity.user?.name) }}
                                <!-- Same reason as the filter: the name alone does not say who. -->
                                <div v-if="activity.user_email" class="text-caption text-medium-emphasis">
                                    {{ activity.user_email }}
                                </div>
                            </td>
                            <td>{{ activity.ip_address }}</td>
                            <td>{{ activity.action }}</td>
                            <td>{{ activity.user_agent }}</td>
                            <td>{{ activity.additional_data }}</td>
                        </tr>
                        <tr v-else>
                            <td v-if="loading" colspan="7">
                                <v-progress-circular color="primary" indeterminate />
                            </td>
                            <td v-else colspan="7">No record found</td>
                        </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination v-model="currentPage" :length="totalPages" class="my-4" density="compact"
                        @click="fetchUserActivityLogs"></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";

const userActivityLogs = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref(null);
const sortedColumn = ref(null);
const {showSnackbar} = useSnackbarStore();
const authStore = useAuthStore();
const user = ref(null);
const users = ref([]);
const action = ref(null);
const actions = ref([]);

onMounted(() => {
    fetchUserActivityLogs();
});

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchUserActivityLogs();
}

/** first + middle + last leaves a double space when there is no middle name. */
const cleanName = (value) => (value || '').replace(/\s+/g, ' ').trim();

/**
 * Names do not identify anyone here. On prod a third of the users share one
 * name, so the list needs the email, and the company for an admin who sees
 * users from several. The id is the last resort that is always unique.
 */
const buildUserOptions = (rows) => {
    const options = (rows || []).map((row) => {
        // getNameAttribute concatenates first, middle and last, so a user with
        // no middle name arrives with a double space in the middle.
        const name = cleanName(row.name) || 'Unnamed user';
        const email = row.email || '';
        const companies = (row.companies || [])
            .map((company) => company.company_name)
            .filter(Boolean);
        return {
            id: row.id,
            name,
            email,
            companies,
            // Everything the autocomplete filters on.
            label: [name, email, ...companies, `#${row.id}`].filter(Boolean).join(' '),
        };
    });

    // Naming the company is only informative when there is more than one to
    // tell apart. For a company admin every row would carry the same value.
    const manyCompanies = new Set(options.flatMap((option) => option.companies)).size > 1;

    return options.map((option) => ({
        ...option,
        detail: [
            option.email,
            manyCompanies ? option.companies.join(', ') : '',
            `#${option.id}`,
        ].filter(Boolean).join('  ·  '),
    }));
};

const fetchUserActivityLogs = () => {
    loading.value = true;
    const params = new URLSearchParams({
        page: currentPage.value,
        ...(user.value && { user_id: user.value }),
        ...(action.value && { action: action.value }),
        ...(sortedColumn.value && { sort: sortedColumn.value }),
        ...(sortedType.value && { order: sortedType.value }),
    });
    send('get', `user-activity-logs?${params.toString()}`).then((response) => {
        userActivityLogs.value = response.data.data.logs.data;
        totalPages.value = response.data.data.logs.last_page;
        currentPage.value = response.data.data.logs.current_page;
        users.value = buildUserOptions(response.data.data.users);
        actions.value = response.data.data.actions;
        loading.value = false;
    })
    .catch((error) => {
        console.log(error)
        showSnackbar('User Activity Logs could not be loaded', 'error')
    })
}
</script>

<style scoped>
</style>
